import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { X, PencilSimple, ClockCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const FIELDS = ["numero", "objeto", "fornecedor", "cnpj_fornecedor", "modalidade", "valor",
  "data_inicio", "data_fim", "status", "municipio_id", "orgao"];
const STATUSES = ["ativo", "em_analise", "suspenso", "encerrado", "vencido"];
const LABELS = { numero: "Número", objeto: "Objeto", fornecedor: "Fornecedor", cnpj_fornecedor: "CNPJ/CPF",
  modalidade: "Modalidade", valor: "Valor", data_inicio: "Início", data_fim: "Fim", status: "Status",
  municipio_id: "Município", municipio_nome: "Município", orgao: "Órgão" };

export const canEditContract = (contract, user) => {
  if (!contract || !user || contract.fonte !== "manual") return false;
  if (user.role === "admin") return true;
  return !!user.municipio_id && contract.municipio_id === user.municipio_id;
};

const initForm = (c) => Object.fromEntries(FIELDS.map((k) => {
  const v = c[k] ?? "";
  return [k, k.startsWith("data_") ? String(v).slice(0, 10) : v];
}));

const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <label className="uppercase-label block mb-2">{label}</label>
    {children}
  </div>
);

const History = ({ items }) => {
  if (!items?.length) return null;
  return (
    <div className="border-t border-[#334155] pt-4" data-testid="ce-history">
      <div className="flex items-center gap-2 uppercase-label !text-[#F59E0B] mb-2">
        <ClockCounterClockwise size={14} /> Histórico de edições
      </div>
      <ul className="space-y-1.5 max-h-36 overflow-y-auto text-[11px] text-[#94A3B8]">
        {[...items].reverse().slice(0, 10).map((h, i) => (
          <li key={i} data-testid={`ce-history-item-${i}`}>
            <span className="font-mono text-[#E9EEF5]">{new Date(h.em).toLocaleString("pt-BR")}</span>
            {" · "}{h.por_nome || h.por}{" · "}
            {Object.keys(h.alteracoes || {}).map((k) => LABELS[k] || k).join(", ")}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default function ContractEditModal({ contract, onClose, onSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState(() => initForm(contract));
  const [saving, setSaving] = useState(false);
  const [modalidades, setModalidades] = useState([]);
  const [munis, setMunis] = useState([]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    api.get("/contracts/facets").then((r) => setModalidades(r.data?.modalidades || [])).catch(() => {});
    if (isAdmin) api.get("/municipalities").then((r) => setMunis(r.data || [])).catch(() => {});
  }, [isAdmin]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.data_inicio && form.data_fim && form.data_fim < form.data_inicio) {
      toast.error("A data de fim não pode ser anterior à data de início");
      return;
    }
    const original = initForm(contract);
    const payload = {};
    FIELDS.forEach((k) => {
      if (k === "municipio_id" && !isAdmin) return;
      const v = k === "valor" ? parseFloat(form.valor) : form[k];
      const o = k === "valor" ? parseFloat(original.valor) : original[k];
      if (v !== o) payload[k] = v;
    });
    if (!Object.keys(payload).length) { toast.info("Nenhuma alteração para salvar"); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/contracts/${contract.id}`, payload);
      toast.success(`Contrato atualizado (${data.alterados.length} campo(s))`);
      onSaved?.(data.contract);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao salvar alterações");
    } finally { setSaving(false); }
  };

  const modOptions = modalidades.includes(form.modalidade) || !form.modalidade
    ? modalidades : [form.modalidade, ...modalidades];
  const inp = "tce-input w-full px-3 py-2.5 rounded-md text-sm";

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="contract-edit-modal" translate="no">
      <div className="tce-card w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#334155]">
          <div>
            <div className="uppercase-label flex items-center gap-1.5"><PencilSimple size={12} /> Edição de contrato</div>
            <div className="font-heading text-xl mt-1">Editar dados do contrato {contract.numero}</div>
          </div>
          <button onClick={onClose} data-testid="ce-close" className="text-[#94A3B8] hover:text-[#F59E0B]"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Número"><input data-testid="ce-numero" required value={form.numero} onChange={set("numero")} className={inp} /></Field>
            <Field label="Modalidade">
              <select data-testid="ce-modalidade" value={form.modalidade} onChange={set("modalidade")} className={inp}>
                {modOptions.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Objeto do contrato">
            <textarea data-testid="ce-objeto" required rows={3} value={form.objeto} onChange={set("objeto")} className={inp} />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Fornecedor"><input data-testid="ce-fornecedor" required value={form.fornecedor} onChange={set("fornecedor")} className={inp} /></Field>
            <Field label="CNPJ/CPF"><input data-testid="ce-cnpj" value={form.cnpj_fornecedor} onChange={set("cnpj_fornecedor")} className={`${inp} font-mono`} /></Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <Field label="Valor total (R$)"><input data-testid="ce-valor" type="number" step="0.01" min="0" required value={form.valor} onChange={set("valor")} className={`${inp} font-mono`} /></Field>
            <Field label="Início vigência"><input data-testid="ce-inicio" type="date" required value={form.data_inicio} onChange={set("data_inicio")} className={inp} /></Field>
            <Field label="Fim vigência"><input data-testid="ce-fim" type="date" required value={form.data_fim} onChange={set("data_fim")} className={inp} /></Field>
            <Field label="Status">
              <select data-testid="ce-status" value={form.status} onChange={set("status")} className={inp}>
                {(STATUSES.includes(form.status) ? STATUSES : [form.status, ...STATUSES]).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Município">
              {isAdmin ? (
                <select data-testid="ce-muni" required value={form.municipio_id} onChange={set("municipio_id")} className={inp}>
                  {!munis.length && <option value={form.municipio_id}>{contract.municipio_nome}</option>}
                  {munis.map((m) => <option key={m.ibge_code} value={m.ibge_code}>{m.name}</option>)}
                </select>
              ) : (
                <input data-testid="ce-muni-readonly" value={contract.municipio_nome || ""} disabled className={`${inp} opacity-60`} />
              )}
            </Field>
            <Field label="Órgão / Secretaria"><input data-testid="ce-orgao" value={form.orgao} onChange={set("orgao")} className={inp} /></Field>
          </div>
          <p className="text-[11px] text-[#94A3B8]">
            Saldo, execuções, aditivos e itens não são alterados por esta edição. Todas as alterações ficam registradas no histórico.
          </p>
          <History items={contract.historico_edicoes} />
          <div className="flex justify-end gap-2 pt-2 border-t border-[#334155]">
            <button type="button" onClick={onClose} data-testid="ce-cancel"
                    className="px-4 py-2 rounded-md border border-[#334155] hover:border-[#94A3B8] text-sm">Cancelar</button>
            <button data-testid="ce-save" disabled={saving}
                    className="px-5 py-2 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] font-medium text-sm disabled:opacity-50">
              {saving ? "Salvando…" : "Salvar alterações"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
